import React, { useCallback, useEffect, useRef, useState } from "react";

import { CircularIndeterminate, FormButton } from "components";
import { FormikAutocomplete, FormikSelectInput, FormikTextInput } from "components/UI/Inputs/FormikInputs";

import { Form, Formik } from "formik";
import * as yup from "yup";

import { useRecoilValue, useSetRecoilState } from "recoil";
import {
    rowSelected,
    snackbarData,
    userCredentials,
} from "recoilState/GlobalState";

import { FORM_ERROR_MESSAGES } from "consts/errorMessages";

import styles from "styles/pages/ModalActions.module.scss";
import { handleResponse } from "services/Local-services";
import { lugarDeviceIns } from "services/services-mia/miakey";
import { Grid } from "@mui/material";

const tipos = [
    { tipoCod: "E", tipoNombre: "Entrada" },
    { tipoCod: "S", tipoNombre: "Salida" },
    { tipoCod: "A", tipoNombre: "Ambos" },
];

export default function InsertModal({ updateList, kioscoId, closeModal, zonas, accesos, currentConfig }) {
    const [inRequest, setInRequest] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [accesosFiltrado, setAccesosFiltrado] = useState([]);

    const setSelected = useSetRecoilState(rowSelected);
    const setSnackbarInfo = useSetRecoilState(snackbarData);
    const credentials = useRecoilValue(userCredentials);

    const formikRef = useRef();

    const selectAcceso = (accesoId) => {
        formikRef.current.setFieldValue("acceso", accesoId);
    };

    const getAccesos = useCallback((zonaId, defaultAccesoId = null) => {
        if (!zonaId) return;
        setIsLoading(true);
        const zonaIdNum = Number(zonaId);
        const result = accesos.filter((a) => a.gateZonaId === zonaIdNum);
        setAccesosFiltrado(result);
        const toSelect = defaultAccesoId && result.some(a => a.gateLugarId === Number(defaultAccesoId))
            ? defaultAccesoId
            : result[0]?.gateLugarId;
        selectAcceso(toSelect ?? "");
        setIsLoading(false);
    }, [accesos]);

    useEffect(() => {
        if (!zonas.length) return;
        const initialZona = currentConfig?.gateZonaId ?? zonas[0].gateZonaId;
        const initialAcceso = currentConfig?.gateLugarId ?? null;
        getAccesos(initialZona, initialAcceso);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getAccesos, zonas]);

    const selectZona = (zonaId) => {
        formikRef.current.setFieldValue("zona", zonaId);
    };

    const handleChangeZona = (selected) => {
        const zonaId = selected?.gateZonaId ?? "";
        selectZona(zonaId);
        getAccesos(zonaId);
    };

    const handleChangeAcceso = (selected) => {
        selectAcceso(selected?.gateLugarId ?? "");
    };

    const handleSubmit = async ({ tipo, zona, acceso, requiereEntrada, requiereSalida }) => {
        setInRequest(true);

        const response = await lugarDeviceIns({
            gateDeviceId: kioscoId,
            gateLugarId: acceso,
            gateLugarDeviceFecha: new Date().toISOString().substring(0, 10),
            gateLugarDeviceStatus: "A",
            gateLugarDeviceTipo: tipo,
            gateDeviceRequiereEntrada: requiereEntrada,
            gateDeviceRequiereSalida: requiereSalida,
        }, credentials);

        handleResponse(
            response,
            currentConfig ? "Configuración actualizada exitosamente" : "Configuración agregada exitosamente",
            { setSnackbarInfo, setIsOpen: closeModal, setSelected, updateList }
        );

        setInRequest(false);
    };

    return (
        <Formik
            innerRef={formikRef}
            initialValues={{
                fecha: new Date().toISOString().substring(0, 10),
                tipo: currentConfig?.gateLugarDeviceTipo ?? "A",
                zona: currentConfig?.gateZonaId ?? zonas[0]?.gateZonaId ?? "",
                acceso: currentConfig?.gateLugarId ?? "",
                requiereEntrada: currentConfig?.gateLugarDeviceRequiereEntrada ?? "N",
                requiereSalida: currentConfig?.gateLugarDeviceRequiereSalida ?? "N",
            }}
            validationSchema={yup.object({
                zona: yup.number(FORM_ERROR_MESSAGES.number).required(FORM_ERROR_MESSAGES.required),
                acceso: yup.number(FORM_ERROR_MESSAGES.number).required(FORM_ERROR_MESSAGES.required),
                tipo: yup.string(FORM_ERROR_MESSAGES.text)
                    .oneOf(tipos.map((t) => t.tipoCod), { message: 'Elija un tipo válido' })
                    .required(FORM_ERROR_MESSAGES.required),
                requiereEntrada: yup.string(FORM_ERROR_MESSAGES.text)
                    .oneOf(["N", "Y"], { message: 'Valor inválido' })
                    .required(FORM_ERROR_MESSAGES.required),
                requiereSalida: yup.string(FORM_ERROR_MESSAGES.text)
                    .oneOf(["N", "Y"], { message: 'Valor inválido' })
                    .required(FORM_ERROR_MESSAGES.required),
            })}
            onSubmit={(values, { setSubmitting }) => {
                try {
                    handleSubmit(values);
                } catch (e) {
                    setSnackbarInfo({ message: e.errmsg, severity: "error", open: true });
                } finally {
                    setSubmitting(false);
                }
            }}
        >
            {(formik) => (
            <Form className={styles.inputs} noValidate>
                <Grid container justifyContent={"center"} spacing={2}>
                    <Grid item md={2} lg={2}>
                        <FormikTextInput
                            name="fecha"
                            labelText={"Fecha"}
                            type="date"
                            disabled
                        />
                    </Grid>
                    <Grid item md={5} lg={5}>
                        <FormikAutocomplete
                            name="zona"
                            labelText="Zona"
                            options={zonas}
                            optionId="gateZonaId"
                            optionLabel="gateZonaNombre"
                            formik={formik}
                            onChange={(e) => handleChangeZona(e)}
                        />
                    </Grid>
                    <Grid item md={5} lg={5}>
                        <FormikAutocomplete
                            name="acceso"
                            labelText="Acceso"
                            options={accesosFiltrado}
                            optionId="gateLugarId"
                            optionLabel="gateLugarNombre"
                            formik={formik}
                            onChange={(e) => handleChangeAcceso(e)}
                        />
                    </Grid>
                    <Grid item md={4} lg={4}>
                        <FormikSelectInput name="tipo" labelText="Tipo">
                            {tipos.map(({ tipoCod, tipoNombre }) => (
                                <option key={tipoCod} data-id={tipoCod} value={tipoCod}>
                                    {tipoNombre}
                                </option>
                            ))}
                        </FormikSelectInput>
                    </Grid>
                    <Grid item md={4} lg={4}>
                        <FormikSelectInput name="requiereEntrada" labelText="Requiere Entrada">
                            <option key="N" data-id="N" value="N"> No </option>
                            <option key="Y" data-id="Y" value="Y"> Sí </option>
                        </FormikSelectInput>
                    </Grid>
                    <Grid item md={4} lg={4}>
                        <FormikSelectInput name="requiereSalida" labelText="Requiere Salida">
                            <option key="N" data-id="N" value="N"> No </option>
                            <option key="Y" data-id="Y" value="Y"> Sí </option>
                        </FormikSelectInput>
                    </Grid>
                    <FormButton
                        inRequest={inRequest || isLoading}
                        newFormData={{ button: currentConfig ? "Guardar" : "Agregar" }}
                        onCloseModal={closeModal}
                    />
                    {(inRequest || isLoading) && <CircularIndeterminate />}
                </Grid>
            </Form>
            )}
        </Formik>
    );
}
